import { format } from "date-fns";
import { Trash2 } from "lucide-react";
import {
  Form,
  minValue,
  required,
  useCreate,
  useDelete,
  useGetIdentity,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useMemo } from "react";
import { useWatch, type FieldValues } from "react-hook-form";

import { DateInput } from "@/components/admin/date-input";
import { DateTimeInput } from "@/components/admin/date-time-input";
import { SaveButton } from "@/components/admin/form";
import { NumberInput } from "@/components/admin/number-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { PersonalTodo, TodoRecurrence, TodoReminder } from "../types";
import {
  TODO_RECURRENCES,
  TODO_REMINDERS,
  computeRemindAt,
  parseDateOnly,
  toDateOnly,
} from "./todoSchedule";
import { useDateLocale } from "./useDateLocale";

const RESOURCE = "personal_todos";
const DEFAULT_CUSTOM_INTERVAL = 2;

const reminderChoices = TODO_REMINDERS.map((id) => ({
  id,
  name: `crm.personal.todos.reminders.${id}`,
}));

const recurrenceChoices = TODO_RECURRENCES.map((id) => ({
  id,
  name: `crm.personal.todos.recurrences.${id}`,
}));

export interface TodoDialogProps {
  open: boolean;
  onClose: () => void;
  /** To-do to edit; when absent the dialog creates a new to-do. */
  todo?: PersonalTodo | null;
}

const isValidDateOnly = (value: unknown): value is string =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

const ReminderPreview = () => {
  const translate = useTranslate();
  const { dateFnsLocale } = useDateLocale();
  const [dueDate, reminder, remindAt] = useWatch({
    name: ["due_date", "reminder", "remind_at"],
  }) as [string, TodoReminder, string | null];

  if (!isValidDateOnly(dueDate) || reminder === "none") return null;
  const preview = computeRemindAt(dueDate, reminder, remindAt);
  if (!preview) return null;

  const isPast = new Date(preview) < new Date();
  return (
    <p
      className={
        isPast ? "text-xs text-destructive" : "text-xs text-muted-foreground"
      }
    >
      {isPast
        ? translate("crm.personal.todos.reminder_in_past")
        : translate("crm.personal.todos.reminder_hint", {
            date: format(new Date(preview), "PPPp", { locale: dateFnsLocale }),
          })}
    </p>
  );
};

const RecurrencePreview = () => {
  const translate = useTranslate();
  const { dateFnsLocale } = useDateLocale();
  const [dueDate, recurrence, interval] = useWatch({
    name: ["due_date", "recurrence", "recurrence_interval"],
  }) as [string, TodoRecurrence, number | null];

  if (!isValidDateOnly(dueDate) || recurrence === "none") return null;
  const day = parseDateOnly(dueDate);
  const options = {
    weekday: format(day, "EEEE", { locale: dateFnsLocale }),
    day: format(day, "d"),
    date: format(day, "d MMMM", { locale: dateFnsLocale }),
    smart_count: Number(interval) || 1,
  };
  return (
    <p className="text-xs text-muted-foreground">
      {translate(`crm.personal.todos.recurrence_hints.${recurrence}`, options)}
    </p>
  );
};

const ConditionalFields = () => {
  const [reminder, recurrence] = useWatch({
    name: ["reminder", "recurrence"],
  }) as [TodoReminder, TodoRecurrence];
  return (
    <>
      <div className="flex flex-col gap-1">
        <SelectInput
          source="reminder"
          label="crm.personal.todos.fields.reminder"
          choices={reminderChoices}
          validate={required()}
        />
        {reminder === "custom" && (
          <DateTimeInput
            source="remind_at"
            label="crm.personal.todos.fields.remind_at"
            validate={required()}
          />
        )}
        <ReminderPreview />
      </div>
      <div className="flex flex-col gap-1">
        <SelectInput
          source="recurrence"
          label="crm.personal.todos.fields.recurrence"
          choices={recurrenceChoices}
          validate={required()}
        />
        {recurrence === "custom" && (
          <NumberInput
            source="recurrence_interval"
            label="crm.personal.todos.fields.recurrence_interval"
            min={1}
            step={1}
            validate={[required(), minValue(1)]}
          />
        )}
        <RecurrencePreview />
      </div>
    </>
  );
};

export const TodoDialog = ({ open, onClose, todo }: TodoDialogProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [create] = useCreate();
  const [update] = useUpdate();
  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const isEdit = !!todo;

  const record = useMemo(
    () =>
      todo
        ? {
            ...todo,
            recurrence_interval:
              todo.recurrence_interval ?? DEFAULT_CUSTOM_INTERVAL,
          }
        : {
            title: "",
            description: "",
            due_date: toDateOnly(new Date()),
            reminder: "none",
            remind_at: null,
            recurrence: "none",
            recurrence_interval: DEFAULT_CUSTOM_INTERVAL,
          },
    [todo],
  );

  const handleError = (error: unknown) =>
    notify(
      error instanceof Error ? error.message : "ra.notification.http_error",
      { type: "error" },
    );

  const handleSubmit = async (values: FieldValues) => {
    const reminder = values.reminder as TodoReminder;
    const recurrence = values.recurrence as TodoRecurrence;
    const remindAt = computeRemindAt(
      values.due_date,
      reminder,
      values.remind_at,
    );
    const data = {
      title: values.title,
      description: values.description || null,
      due_date: values.due_date,
      reminder,
      remind_at: remindAt,
      recurrence,
      recurrence_interval:
        recurrence === "custom" ? Number(values.recurrence_interval) : null,
      // A changed reminder must fire again
      reminded_at:
        todo && todo.remind_at === remindAt ? (todo.reminded_at ?? null) : null,
    };

    if (todo) {
      await update(
        RESOURCE,
        { id: todo.id, data, previousData: todo },
        {
          onSuccess: () => {
            notify("crm.personal.todos.updated");
            onClose();
          },
          onError: handleError,
        },
      );
      return;
    }
    await create(
      RESOURCE,
      { data: { ...data, done_at: null, sales_id: identity?.id } },
      {
        onSuccess: () => {
          notify("crm.personal.todos.created");
          onClose();
        },
        onError: handleError,
      },
    );
  };

  const handleDelete = () => {
    if (!todo) return;
    deleteOne(
      RESOURCE,
      { id: todo.id, previousData: todo },
      {
        onSuccess: () => {
          notify("crm.personal.todos.deleted");
          onClose();
        },
        onError: handleError,
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="lg:max-w-lg overflow-y-auto max-h-9/10">
        {open && (
          <Form
            record={record}
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <DialogHeader>
              <DialogTitle>
                {translate(
                  isEdit
                    ? "crm.personal.todos.edit"
                    : "crm.personal.todos.create",
                )}
              </DialogTitle>
            </DialogHeader>
            <TextInput
              source="title"
              label="crm.personal.todos.fields.title"
              validate={required()}
              autoFocus
            />
            <TextInput
              source="description"
              label="crm.personal.todos.fields.description"
              multiline
            />
            <DateInput
              source="due_date"
              label="crm.personal.todos.fields.due_date"
              validate={required()}
            />
            <ConditionalFields />
            <DialogFooter className="w-full gap-2 sm:justify-between">
              {isEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="text-destructive"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  <Trash2 />
                  {translate("crm.personal.todos.delete")}
                </Button>
              ) : (
                <span />
              )}
              <SaveButton />
            </DialogFooter>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
};
